import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SEOContentHubHeader from './seo-content-hub/SEOContentHubHeader';
import TopicalAuthorityTab from './seo-content-hub/TopicalAuthorityTab';
import BlogCreatorTab from './seo-content-hub/BlogCreatorTab';
import ContentLibraryTab from './seo-content-hub/ContentLibraryTab';

export type SEOContentHubTab = 'topical-authority' | 'blog-creator' | 'content-library';

const SEOContentHub: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState<SEOContentHubTab>('topical-authority');
  const [selectedTitleData, setSelectedTitleData] = useState<any>(null);

  // Parse hash from URL to set active tab
  useEffect(() => {
    const hash = location.hash.slice(1) as SEOContentHubTab;
    if (['topical-authority', 'blog-creator', 'content-library'].includes(hash)) {
      setActiveTab(hash);
    } else {
      setActiveTab('topical-authority');
    }
  }, [location.hash]);

  // Update URL hash when tab changes
  const handleTabChange = (tab: SEOContentHubTab) => {
    setActiveTab(tab);
    navigate(`#${tab}`, { replace: true });
  };

  // Handle switching to blog creator with data
  const handleGeneratePost = (titleData: any) => {
    setSelectedTitleData(titleData);
    handleTabChange('blog-creator');
  };

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <SEOContentHubHeader />

        {/* Tab Navigation */}
        <div className="mt-6 rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="border-b border-gray-200 px-6">
            <nav className="flex gap-6" aria-label="Tabs">
              <button
                onClick={() => handleTabChange('topical-authority')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition
                  ${activeTab === 'topical-authority'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
                aria-current={activeTab === 'topical-authority' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span>Topical Authority</span>
                <span className="ml-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                  Strategy
                </span>
              </button>
              
              <button
                onClick={() => handleTabChange('blog-creator')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition
                  ${activeTab === 'blog-creator'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
                aria-current={activeTab === 'blog-creator' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span>Blog Creator</span>
                <span className="ml-1 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                  Create
                </span>
              </button>

              <button
                onClick={() => handleTabChange('content-library')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition
                  ${activeTab === 'content-library'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
                aria-current={activeTab === 'content-library' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span>Content Library</span>
                <span className="ml-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                  Manage
                </span>
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'topical-authority' && (
              <TopicalAuthorityTab onGeneratePost={handleGeneratePost} />
            )}
            {activeTab === 'blog-creator' && (
              <BlogCreatorTab prefilledData={selectedTitleData} onDataUsed={() => setSelectedTitleData(null)} />
            )}
            {activeTab === 'content-library' && (
              <ContentLibraryTab />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SEOContentHub;
